import React, { type JSX } from "react";
import type { Expert } from "./types/redux";

interface InlineExpertsTableProps {
  experts: Expert[];
  onExpertClick: (expert: Expert) => void;
}

const MAX_VISIBLE_ROWS = 10;
const MAX_TOP_DEALS = 3;

const HEADERS: { key: string; label: string; className?: string }[] = [
  { key: "rank", label: "#", className: "w-10" },
  { key: "name", label: "Name", className: "min-w-[160px]" },
  { key: "score", label: "Score", className: "w-16" },
  { key: "deals", label: "Deals", className: "w-16" },
  { key: "topDeals", label: "Top deals", className: "min-w-[200px]" },
];

function subline(expert: Expert): string {
  return [expert.jobTitle, expert.team].filter(Boolean).join(" · ");
}

function TopDeals({ expert }: { expert: Expert }): JSX.Element {
  const names = expert.topDeals.map((d) => d.dealName).filter((n): n is string => !!n);
  if (names.length === 0) return <span className="text-gray-400">--</span>;
  const extra = names.length - MAX_TOP_DEALS;
  return (
    <span>
      {names.slice(0, MAX_TOP_DEALS).join(", ")}
      {extra > 0 && (
        <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-cvc-tableHeader text-xs text-cvc-headerText">
          +{extra}
        </span>
      )}
    </span>
  );
}

function ExpertRow({
  expert,
  onExpertClick,
}: {
  expert: Expert;
  onExpertClick: (expert: Expert) => void;
}): JSX.Element {
  const sub = subline(expert);
  return (
    <tr
      className="hover:bg-cvc-lb2 transition-colors duration-150 cursor-pointer"
      onClick={() => onExpertClick(expert)}
    >
      <td className="py-2 px-3 text-sm text-cvc-text">{expert.rank}</td>
      <td className="py-2 px-3 text-sm text-cvc-text">
        {/* A real button so keyboard users can open the profile (Enter/Space);
            the row's onClick keeps the whole row clickable with a pointer. */}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onExpertClick(expert);
          }}
          className="text-cvc-blue font-medium text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cvc-blue rounded-[3px]"
        >
          {expert.personDisplayName}
        </button>
        {sub && <div className="text-xs text-cvc-headerText">{sub}</div>}
      </td>
      <td className="py-2 px-3 text-sm text-cvc-text">
        {expert.score == null ? <span className="text-gray-400">--</span> : Math.round(expert.score)}
      </td>
      <td className="py-2 px-3 text-sm text-cvc-text">{expert.matchedDealCount}</td>
      <td className="py-2 px-3 text-sm text-cvc-text">
        <TopDeals expert={expert} />
      </td>
    </tr>
  );
}

export default function InlineExpertsTable({
  experts,
  onExpertClick,
}: InlineExpertsTableProps): JSX.Element | null {
  if (experts.length === 0) return null;

  return (
    <div className="mt-3 mb-1 border border-cvc-tableBorder rounded-[5px] overflow-hidden">
      <div className="overflow-auto max-h-[440px]">
        <table className="min-w-full divide-y divide-cvc-tableBorder">
          <thead className="bg-cvc-tableHeader sticky top-0 z-10">
            <tr>
              {HEADERS.map((h) => (
                <th
                  key={h.key}
                  scope="col"
                  className={`py-2 px-3 text-left text-xs font-cvcBody font-normal text-cvc-headerText uppercase tracking-wider ${h.className || ""}`}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-cvc-tableBorder font-cvcBody">
            {experts.map((expert) => (
              <ExpertRow key={expert.personKey} expert={expert} onExpertClick={onExpertClick} />
            ))}
          </tbody>
        </table>
      </div>
      {experts.length > MAX_VISIBLE_ROWS && (
        <div className="px-3 py-2 bg-cvc-tableHeader border-t border-cvc-tableBorder text-xs text-cvc-headerText text-right">
          Showing {MAX_VISIBLE_ROWS} of {experts.length} — scroll for more
        </div>
      )}
    </div>
  );
}
